// ============================================================================
// CopyReference — AFB-0030 (Jose). A tiny "Copy ref" affordance that puts a
// traceable, linkable reference for a record on the clipboard so Finance can
// paste it onto the (external) AP worksheet and trace any line back to its
// source record. Copies "<ID> — <absolute deep link>" (optionally with a short
// detail like the pay amount). Used on project-work payments and sub changes.
// Frontend-only; no schema. Falls back to a prompt when the clipboard API is
// unavailable (insecure context / blocked permissions).
// ============================================================================

import { useState } from 'react';
import { Check, Copy } from 'lucide-react';

interface Props {
  /** Canonical record id, e.g. PRJ-0123 / SRR-0007. Shown + copied. */
  id: string;
  /** App-relative deep link to the record, e.g. /project-work/PRJ-0123. */
  path: string;
  /** Optional short context appended to the copied text (e.g. "pay $250"). */
  detail?: string;
  className?: string;
}

export function CopyReference({ id, path, detail, className = '' }: Props) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    const url =
      typeof window !== 'undefined' ? `${window.location.origin}${path}` : path;
    const text = detail ? `${id} (${detail}) — ${url}` : `${id} — ${url}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (insecure context / denied) — surface for manual copy.
      window.prompt('Copy this AP reference:', text);
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      title="Copy a linkable reference for the AP worksheet"
      aria-label={`Copy reference for ${id}`}
      className={`inline-flex items-center gap-1 rounded border border-tcs-border bg-tcs-raised px-1.5 py-0.5 text-[11px] font-normal text-tcs-text-secondary hover:bg-tcs-bg-muted ${className}`}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy ref'}
    </button>
  );
}
